"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { gsap, PLAY_ONCE, useGSAP } from "@/lib/gsap";
import { reduceMotion } from "@/lib/motion";
import { cn, seeded } from "@/lib/utils";
import { ArrowUpRight, Clock, Receipt } from "@/components/ui/icons";

const RANGES = ["1H", "1D", "1W", "1M", "All"] as const;
type Range = (typeof RANGES)[number];

const SEEDS: Record<Range, number> = { "1H": 11, "1D": 23, "1W": 7, "1M": 41, All: 5 };
const X_LABELS: Record<Range, string[]> = {
  "1H": ["2:10 PM", "2:20 PM", "2:30 PM", "2:40 PM", "2:50 PM", "3:00 PM"],
  "1D": ["6 AM", "9 AM", "12 PM", "3 PM", "6 PM", "9 PM"],
  "1W": ["5:24 PM", "8:33 PM", "1:42 AM", "5:52 AM", "10:01 AM", "2:13 PM"],
  "1M": ["Sep 1", "Sep 7", "Sep 13", "Sep 19", "Sep 25", "Oct 1"],
  All: ["2021", "2022", "2023", "2024", "2025", "2026"],
};

// Chart geometry (SVG user units)
const W = 600;
const LEFT = 34;
const TOP = 8;
const BOTTOM = 158;
const MAX = 24;

/** Spiky, market-like random walk — seeded so server and client render identical paths. */
function series(seed: number, n = 118) {
  const rand = seeded(seed);
  const out: number[] = [];
  let v = 7 + rand() * 4;
  for (let i = 0; i < n; i++) {
    v += (rand() - 0.5) * 2.4;
    if (rand() > 0.93) v += 3 + rand() * 7;
    v = v * 0.95 + 9 * 0.05;
    v = Math.min(21, Math.max(2.5, v));
    out.push(v);
  }
  return out;
}

function paths(values: number[]) {
  const span = W - LEFT;
  const pts = values.map((v, i) => [LEFT + (i / (values.length - 1)) * span, BOTTOM - (v / MAX) * (BOTTOM - TOP)]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  return { line, area: `${line} L${W} ${BOTTOM} L${LEFT} ${BOTTOM} Z` };
}

/** Statistics widget: range tabs, a drawn-in area chart and a KPI strip. */
export function AnalyticsCard({ className }: { className?: string }) {
  const uid = useId().replace(/:/g, "");
  const root = useRef<HTMLDivElement>(null);
  const clip = useRef<SVGRectElement>(null);
  const drawn = useRef(false);
  const [range, setRange] = useState<Range>("1W");
  const { line, area } = useMemo(() => paths(series(SEEDS[range])), [range]);

  useGSAP(
    () => {
      const rect = clip.current;
      if (!rect) return;
      if (reduceMotion()) {
        gsap.set(rect, { attr: { width: W } });
        drawn.current = true;
        return;
      }
      gsap.fromTo(
        rect,
        { attr: { width: 0 } },
        {
          attr: { width: W },
          duration: 2.1,
          ease: "glide",
          scrollTrigger: { trigger: root.current, start: "top 72%", toggleActions: PLAY_ONCE },
          onComplete: () => {
            drawn.current = true;
          },
        },
      );
    },
    { scope: root },
  );

  useEffect(() => {
    if (!drawn.current || !clip.current) return;
    gsap.fromTo(clip.current, { attr: { width: 0 } }, { attr: { width: W }, duration: 1.1, ease: "glide" });
  }, [range]);

  const yTicks = [24, 16, 8, 0];

  return (
    <div ref={root} className={cn("rounded-[20px] bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.03)] md:p-5", className)}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-[14px] font-medium">Conversion rate</p>
        <div role="tablist" aria-label="Chart range" className="flex rounded-full bg-panel p-[3px]">
          {RANGES.map((r) => (
            <button
              key={r}
              role="tab"
              type="button"
              aria-selected={range === r}
              onClick={() => setRange(r)}
              className="relative h-6 rounded-full px-2.5 text-[10.5px] font-medium"
            >
              {range === r && (
                <motion.span
                  layoutId={`range-${uid}`}
                  className="absolute inset-0 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                  transition={{ type: "spring", duration: 0.4, bounce: 0.12 }}
                />
              )}
              <span className={cn("relative transition-colors", range === r ? "text-ink" : "text-muted")}>{r}</span>
            </button>
          ))}
        </div>
      </div>

      <svg viewBox={`0 0 ${W} 186`} className="mt-4 block h-auto w-full" role="img" aria-label="Conversion rate chart">
        <defs>
          <linearGradient id={`fill-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2dbfb2" stopOpacity="0.36" />
            <stop offset="1" stopColor="#2dbfb2" stopOpacity="0" />
          </linearGradient>
          <clipPath id={`clip-${uid}`}>
            <rect ref={clip} x="0" y="0" width={W} height="186" />
          </clipPath>
        </defs>
        {yTicks.map((t) => {
          const y = BOTTOM - (t / MAX) * (BOTTOM - TOP);
          return (
            <g key={t}>
              <line x1={LEFT} x2={W} y1={y} y2={y} stroke="#efeff1" strokeDasharray="3 5" />
              <text x="0" y={y + 3.5} fontSize="10" fill="#a1a1a6">
                {t}%
              </text>
            </g>
          );
        })}
        <g clipPath={`url(#clip-${uid})`}>
          <path d={area} fill={`url(#fill-${uid})`} />
          <path d={line} fill="none" stroke="#1fb5a7" strokeWidth="1.5" strokeLinejoin="round" />
        </g>
        {X_LABELS[range].map((label, i) => (
          <text
            key={label}
            x={LEFT + (i / 5) * (W - LEFT - 40)}
            y="182"
            fontSize="10"
            fill="#a1a1a6"
          >
            {label}
          </text>
        ))}
      </svg>

      <div className="mt-4 grid grid-cols-3 gap-2 rounded-[14px] bg-panel p-1.5">
        {[
          { Icon: ArrowUpRight, label: "Leads MTD", value: "1,284" },
          { Icon: Receipt, label: "Cost / lead", value: "$6.54" },
          { Icon: Clock, label: "ROAS", value: "3.4×" },
        ].map(({ Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2 rounded-[10px] bg-white px-2.5 py-2">
            <Icon className="size-3.5 shrink-0 text-muted" />
            <div className="min-w-0">
              <p className="truncate text-[10px] text-muted">{label}</p>
              <p className="text-[12px] font-medium">{value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
